/**
 * @file WmsDashboardQueue.tsx
 * @description Блок «Очередь сборки» «Обзора склада» (phase19.24 этап 1,
 * шаг 3; phase19.31 шаг О4 — строки списка вместо карточек; уточнение
 * владельца к макету (в2): блок слева под «Что нужно сделать» и виден ВСЕГДА).
 *
 * Сборка — основная ежедневная работа склада, поэтому правило «пустой блок
 * не показываем» на очередь не распространяется: пустая очередь —
 * компактная строка «Заданий на сборку нет» с пояснением, без большой рамки.
 *
 * Строки — те же, что в очереди «Заданий на сборку» на телефоне (колонки
 * `pickingQueueColumns`, раскладка «список» на любой ширине): номер заказа
 * и статус, ниже состав, ниже «N из M позиций · способ получения ·
 * исполнитель»; строка — ссылка на задание. Сервер отдаёт до пяти заданий;
 * если в очереди больше — «Ещё N — все задания ›».
 */

import { ChevronRight, ClipboardList } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { DASHBOARD_TEXT } from "./dashboard-texts";
import { pickingQueueColumns } from "../picking/picking-columns";
import { ResponsiveList } from "../../ui/ResponsiveList";
import { SectionCard } from "../../ui/SectionCard";
import type { PickingTaskListItemDto } from "../../../lib/api-picking";

interface Props {
  tasks: PickingTaskListItemDto[];
  /** Сколько заданий в очереди всего (в работе, ожидании и с недостачей). */
  total: number;
  /** База маршрута WMS: `/shop/:slug/wms`. */
  basePath: string;
  /** Текущий пользователь — подпись «вы» у своих заданий. */
  currentUserId: string | null;
  loading: boolean;
}

/** Пустая очередь — одна спокойная строка, а не большая рамка. */
function QueueEmpty() {
  return (
    <div className="flex items-start gap-3 py-1">
      <ClipboardList aria-hidden className="mt-0.5 size-5 shrink-0 text-on-surface-variant" />
      <span className="flex min-w-0 flex-col">
        <span className="type-body font-medium text-on-surface">{DASHBOARD_TEXT.queueEmpty}</span>
        <span className="type-caption text-on-surface-variant">{DASHBOARD_TEXT.queueEmptyHint}</span>
      </span>
    </div>
  );
}

export function WmsDashboardQueue({ tasks, total, basePath, currentUserId, loading }: Props) {
  const columns = useMemo(() => pickingQueueColumns(currentUserId), [currentUserId]);
  const allPath = `${basePath}/picking`;
  const more = total - tasks.length;

  return (
    <SectionCard title={DASHBOARD_TEXT.queueTitle} link={{ label: DASHBOARD_TEXT.queueAll, to: allPath }}>
      {!loading && tasks.length === 0 ? (
        <QueueEmpty />
      ) : (
        <ResponsiveList
          aria-label={DASHBOARD_TEXT.queueTitle}
          layout="compact"
          compactRowSize="tall"
          framed={false}
          rows={tasks}
          columns={columns}
          rowKey={(task) => task.id}
          rowHref={(task) => `${basePath}/picking/${task.id}`}
          rowLabel={(task) => `Задание по заказу ${task.orderNumber}`}
          loading={loading}
          skeletonRows={2}
          empty={{ icon: ClipboardList, title: DASHBOARD_TEXT.queueEmpty }}
        />
      )}
      {!loading && more > 0 ? (
        <Link
          to={allPath}
          className="type-body inline-flex min-h-[var(--control-compact-h,3rem)] items-center gap-1 self-start rounded-md font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          Ещё {more} — все задания
          <ChevronRight aria-hidden className="size-4" />
        </Link>
      ) : null}
    </SectionCard>
  );
}
